import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from 'shared/api/client';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { permissionGroupKeys } from './keys';

// Hooks that additionally read the /registry contract (user lookups) live in
// ./cross-contract-queries.ts and stay on the raw request wrapper until that
// contract is consumed (FUTURE_REFACTORS item 10).

// -- Document-level permissions --

export const useDocumentPermissions = (documentId: number | string) =>
  useQuery({
    queryKey: permissionGroupKeys.documentPermissions(documentId),

    queryFn: async () => {
      const { data } = await api.GET('/documents/{superId}/permissions', { params: { path: { superId: Number(documentId) } } });
      return data;
    },

    enabled: !!documentId,
  });

export const useRemoveDocumentPermissionUser = () =>
  useMutation({
    mutationFn: async ({ documentId, permission, userId }: { documentId: number | string; permission: string; userId: number }) => {
      await api.DELETE('/documents/{superId}/permissions/{permission}/users/{userId}', {
        params: { path: { superId: Number(documentId), permission, userId } },
      });
    },

    onError: ServerErrorHandler,
  });

// -- Permission groups (section-level) --

export const useCreatePermissionGroup = () =>
  useMutation({
    mutationFn: async ({ name, documentSuperId }: { name: string; documentSuperId: number | string }) => {
      const { data } = await api.POST('/permission-groups/', { body: { name, documentSuperId: Number(documentSuperId) } });
      return data;
    },

    onError: ServerErrorHandler,
  });

export const useUpdatePermissionGroup = () =>
  useMutation({
    mutationFn: async ({ id, name, documentSuperId }: { id: number; name: string; documentSuperId: number | string }) => {
      const { data } = await api.PUT('/permission-groups/{permissionGroup}', {
        params: { path: { permissionGroup: id } },
        body: { name, documentSuperId: Number(documentSuperId) },
      });
      return data;
    },

    onError: ServerErrorHandler,
  });

export const useDeletePermissionGroup = () =>
  useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      await api.DELETE('/permission-groups/{permissionGroup}', { params: { path: { permissionGroup: id } } });
    },

    onError: ServerErrorHandler,
  });

export const useRemoveUserFromPermissionGroup = () =>
  useMutation({
    mutationFn: async ({ permissionGroupId, userId }: { permissionGroupId: number; userId: number }) => {
      await api.DELETE('/permission-groups/{permissionGroup}/users/{userId}', { params: { path: { permissionGroup: permissionGroupId, userId } } });
    },

    onError: ServerErrorHandler,
  });

export const useChangeSectionPermission = () =>
  useMutation({
    mutationFn: async ({
      sectionSuperId,
      userId,
      permission,
      currentPermission,
    }: {
      sectionSuperId: string;
      userId: number;
      permission: string;
      currentPermission?: string;
    }) => {
      if (permission === 'NO_PERMISSION' && currentPermission) {
        await api.DELETE('/sections/{superId}/permissions/{permission}/users/{userId}', {
          params: { path: { superId: sectionSuperId, permission: currentPermission, userId } },
        });
      } else if (permission !== 'NO_PERMISSION') {
        // `permission` is already in the path; the contract body is just { userId }.
        await api.PUT('/sections/{superId}/permissions/{permission}/users', {
          params: { path: { superId: sectionSuperId, permission } },
          body: { userId },
        });
      }
    },

    onError: ServerErrorHandler,
  });
